import { Controller } from "@hotwired/stimulus"

// Handles template selection in the defer/handoff modal.
// When a template is selected, shows a step panel with "Generate with AI" or "Use as-is".
// The form submit button is hidden until the user completes this step.
export default class extends Controller {
  static targets = [
    "select", "title", "instructions",
    "proposal", "proposalSubject", "proposalBody",
    "config", "submitButton",
    "stepPanel", "stepActions", "generating",
    "contentFieldset", "noTemplateFields"
  ]

  connect() {
    this.csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    this._loadConfig()
    this._synthesizeAbort = null
    this._initializeSelectedState()
  }

  disconnect() {
    this._synthesizeAbort?.abort()
  }

  _loadConfig() {
    if (!this.hasConfigTarget) return
    try {
      this.templates = JSON.parse(this.configTarget.dataset.templates || "[]")
    } catch { this.templates = [] }
    this.synthesizeUrl = this.configTarget.dataset.synthesizeUrl || ""
    this.handoffType = this.configTarget.dataset.handoffType || ""
  }

  select() {
    this._synthesizeAbort?.abort()
    this._hideProposal()

    const key = this.selectTarget.value
    if (!key) {
      // No template — show standalone fields, hide fieldset
      this._hideContentFieldset()
      this._showNoTemplateFields()
      this._showSubmitButton()
      return
    }

    const template = (this.templates || []).find(t => t.key === key)
    if (!template) return

    // Show the template fieldset, hide standalone fields
    this._showContentFieldset()
    this._hideNoTemplateFields()

    // Fill the fields inside the fieldset
    if (this.hasTitleTarget && template.subject) {
      this.titleTarget.value = template.subject
    }
    if (this.hasInstructionsTarget && template.instructions) {
      this.instructionsTarget.value = template.instructions
    }

    // Show submit — the AI step is optional, not blocking
    this._showSubmitButton()
    // Reset the step panel actions
    if (this.hasStepActionsTarget) this.stepActionsTarget.classList.remove("hidden")
    if (this.hasGeneratingTarget) this.generatingTarget.classList.add("hidden")
  }

  _initializeSelectedState() {
    if (!this.hasSelectTarget) return

    if (this.selectTarget.value) {
      this._showContentFieldset()
      this._hideNoTemplateFields()
      this._showSubmitButton()
      this._showStepPanel()
    } else {
      this._hideContentFieldset()
      this._showNoTemplateFields()
      this._showSubmitButton()
    }
  }

  generate(event) {
    event?.preventDefault()
    const key = this.selectTarget.value
    if (!key) return
    this._synthesize(key)
  }

  skipGenerate(event) {
    event?.preventDefault()
    // Hide just the step panel (AI controls), keep the fieldset visible
    if (this.hasStepPanelTarget) this.stepPanelTarget.classList.add("hidden")
  }

  async _synthesize(key) {
    if (!this.synthesizeUrl) return

    // Show generating state
    if (this.hasStepActionsTarget) this.stepActionsTarget.classList.add("hidden")
    if (this.hasGeneratingTarget) this.generatingTarget.classList.remove("hidden")

    const abort = new AbortController()
    this._synthesizeAbort = abort

    try {
      const response = await fetch(this.synthesizeUrl, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": this.csrfToken,
          "Accept": "application/json",
        },
        body: JSON.stringify({ template_key: key, handoff_type: this.handoffType || "" }),
        signal: abort.signal,
      })

      if (response.ok) {
        const data = await response.json()
        if (data.instructions || data.subject) {
          this._showProposal(data.subject, data.instructions)
        } else {
          this._showStepActions()
        }
      } else {
        this._showStepActions()
      }
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error("Template synthesis failed:", error)
        this._showStepActions()
      }
    } finally {
      if (this.hasGeneratingTarget) this.generatingTarget.classList.add("hidden")
    }
  }

  acceptProposal(event) {
    event?.preventDefault()
    if (this._proposedSubject && this.hasTitleTarget) {
      this.titleTarget.value = this._proposedSubject
    }
    if (this._proposedInstructions && this.hasInstructionsTarget) {
      this.instructionsTarget.value = this._proposedInstructions
    }
    // Hide the AI controls, keep fieldset visible with updated values
    if (this.hasStepPanelTarget) this.stepPanelTarget.classList.add("hidden")
  }

  dismissProposal(event) {
    event?.preventDefault()
    this._hideProposal()
    this._showStepActions()
  }

  _showProposal(subject, instructions) {
    this._proposedSubject = subject || ""
    this._proposedInstructions = instructions || ""
    if (this.hasProposalTarget) {
      if (this.hasProposalSubjectTarget) this.proposalSubjectTarget.textContent = subject || ""
      if (this.hasProposalBodyTarget) this.proposalBodyTarget.textContent = instructions || ""
      this.proposalTarget.classList.remove("hidden")
    }
  }

  _hideProposal() {
    this._proposedSubject = null
    this._proposedInstructions = null
    if (this.hasProposalTarget) this.proposalTarget.classList.add("hidden")
  }

  _showStepPanel() {
    if (this.hasStepPanelTarget) this.stepPanelTarget.classList.remove("hidden")
    this._showStepActions()
    this._hideProposal()
  }

  _hideStepPanel() {
    if (this.hasStepPanelTarget) this.stepPanelTarget.classList.add("hidden")
    if (this.hasGeneratingTarget) this.generatingTarget.classList.add("hidden")
    this._hideProposal()
  }

  _showStepActions() {
    if (this.hasStepActionsTarget) this.stepActionsTarget.classList.remove("hidden")
  }

  _showSubmitButton() {
    if (this.hasSubmitButtonTarget) this.submitButtonTarget.classList.remove("hidden")
  }

  _hideSubmitButton() {
    if (this.hasSubmitButtonTarget) this.submitButtonTarget.classList.add("hidden")
  }

  _showContentFieldset() {
    if (this.hasContentFieldsetTarget) {
      this.contentFieldsetTarget.classList.remove("hidden")
      this.contentFieldsetTarget.querySelectorAll("input, textarea, select").forEach(el => el.disabled = false)
    }
  }

  _hideContentFieldset() {
    if (this.hasContentFieldsetTarget) {
      this.contentFieldsetTarget.classList.add("hidden")
      this.contentFieldsetTarget.querySelectorAll("input, textarea, select").forEach(el => el.disabled = true)
    }
  }

  _showNoTemplateFields() {
    if (this.hasNoTemplateFieldsTarget) {
      this.noTemplateFieldsTarget.classList.remove("hidden")
      this.noTemplateFieldsTarget.querySelectorAll("input, textarea, select").forEach(el => el.disabled = false)
    }
  }

  _hideNoTemplateFields() {
    if (this.hasNoTemplateFieldsTarget) {
      this.noTemplateFieldsTarget.classList.add("hidden")
      this.noTemplateFieldsTarget.querySelectorAll("input, textarea, select").forEach(el => el.disabled = true)
    }
  }
}
